import type { CompanyTab } from '../areas/staff/companyTabs';
import type { EsocialTab } from '../areas/staff/esocialTabs';
import type { Area } from '../session';

/**
 * Every URL in the application, in one place.
 *
 * Paths are pt-BR because the people using this read pt-BR, and a URL is copy
 * too. They are also deliberately free of identifiers that mean anything about
 * a person: a URL ends up in browser history, in a bookmark, in a screen share
 * and in the referrer of anything the page loads. `/empresa/beneficiarios/:id`
 * carries an opaque uuid and nothing else -- never a CPF, never a name.
 */
export const PATHS = {
  root: '/',
  login: '/entrar',
  staff: '/equipe',
  staffReview: '/equipe/analise',
  // The monthly rhythm's list, and one contract's month at its own address.
  // "Abrir" used to open the panels underneath the list, which meant the URL
  // never changed: on a long list an analyst scrolled up and down between the
  // line they were working on and the panel it opened, and could not send a
  // colleague the contract they were looking at. The contract now has a page
  // of its own, carrying the opaque contract uuid and nothing else.
  staffSubmissions: '/equipe/envios',
  staffSubmissionContract: '/equipe/envios/:contratoId',
  staffSubmissionConsolidated: '/equipe/envios/empresas/:empresaId/operadoras/:operadoraId',
  staffSubmissionGroup: '/equipe/envios/grupos/:grupoId/operadoras/:operadoraId',
  // pt-BR for "inbox". The bridge of #221 is a screen of its own rather than a
  // panel on another, because it is a queue somebody works through and because
  // the day it is empty for a month is the day the screen and the bridge both go.
  staffMailbox: '/equipe/caixa-de-entrada',
  staffCompanies: '/equipe/empresas',
  // Registering one is its own address rather than a form sitting above the
  // list. A screen reached from the menu answers "who is already a customer?",
  // and a form permanently open over that answer is an invitation to start
  // typing into it -- see the same pair on every other registry below.
  staffCompanyNew: '/equipe/empresas/nova',
  // One company, bookmarkable and reloadable. It carries the opaque company
  // uuid and nothing else: no CNPJ, no razão social, nothing that names the
  // employer in a history entry or a screen share. The rule at the top of this
  // file is about people, and a company is a party to a contract rather than a
  // person -- but its CNPJ is still the key the Receita files it under, and a
  // slug is still a name.
  staffCompany: '/equipe/empresas/:empresaId',
  // Creating a contract lives under the company it belongs to, because
  // `POST /contracts` takes its company from the `X-Company-Id` scope header
  // and not from the body: a form that was not already inside one company
  // would have to ask, and there is no field for the answer.
  staffCompanyContractNew: '/equipe/empresas/:empresaId/contratos/novo',
  // One segurado of one company, every contract they are under (#418). Under
  // the company because the company is what scopes every read the page makes,
  // as `X-Company-Id`. The person is the opaque `insured_person_id` of any one
  // of their rows and nothing else -- never a CPF, never a name, for the reasons
  // at the top of this file and the ones `customerPerson` gives below.
  staffCompanyPerson: '/equipe/empresas/:empresaId/segurados/:pessoaId',
  // Economic groups (*grupos econômicos*): companies of one economic group,
  // linked for the people who work them and for nothing else -- a group is not
  // a tenant and changes nobody's access. Beside the companies rather than
  // under them, because a group is a registry of its own with its own name and
  // slug, and the same list/new/detail triple as every other registry here.
  // One group carries its opaque uuid and nothing else, like a company.
  staffGroups: '/equipe/grupos',
  staffGroupNew: '/equipe/grupos/novo',
  staffGroup: '/equipe/grupos/:grupoId',
  // Insurers, which belong to no customer company. Their own pair of addresses
  // and their own menu entry rather than a panel on the companies screen: a
  // contract points at one, so somebody onboarding a customer needs to be able
  // to go and register one, and a panel that only exists halfway down another
  // screen is a thing people learn by accident.
  staffInsurers: '/equipe/operadoras',
  staffInsurerNew: '/equipe/operadoras/nova',
  staffCredentials: '/equipe/credenciais',
  staffCredentialNew: '/equipe/credenciais/nova',
  // Next door to `/equipe/credenciais` and deliberately not part of it. That
  // address is credentials a partner holds toward *us*; this one is the
  // credential *we* hold toward the government -- the A1 certificate the
  // eSocial client signs with. Same idea, opposite direction, different hands:
  // a machine credential is revoked when a partner integration ends, and this
  // one is replaced when a certification authority's year is up.
  staffCertificates: '/equipe/certificado-digital',
  staffCertificateNew: '/equipe/certificado-digital/novo',
  // The connection the certificate above is presented on. Its own address
  // rather than a panel on that screen, because they answer different
  // questions on different days: that one is "what do we sign as, and until
  // when?", and this one is "can we reach the government right now?". The
  // second is asked when something has stopped working, by somebody who does
  // not necessarily administer the first.
  staffEsocial: '/equipe/conexao-esocial',
  // What the connection above is *for*: the events the government already holds
  // about a customer's workforce, fetched on demand and staged. Its own address
  // rather than a panel on the connection screen, because the two are read by
  // the same people on different days -- that one is asked when something has
  // stopped working, and this one is worked through when it is working.
  //
  // No identifier of any kind travels in it. The company being looked at is a
  // selector on the page and the event being inspected is an opaque uuid in a
  // request, never in the address -- these rows name people, and a URL ends up
  // in history, in a bookmark and in a screen share.
  staffEsocialEvents: '/equipe/eventos-esocial',
  // The people, not the machines. `/equipe/credenciais` next door is the same
  // idea for systems, and the two are deliberately separate addresses: a
  // machine credential and a person who signs in are revoked for different
  // reasons, by different hands, on different days.
  staffUsers: '/equipe/usuarios',
  staffUserNew: '/equipe/usuarios/novo',
  // The trail of #208, read-only. No filter and no identifier travels in this
  // address -- not the record being investigated, not the person who changed
  // it. The viewer keeps its filters in the component for that reason: the
  // whole point of this file's rule is that a URL ends up in history, in a
  // bookmark and in a screen share, and "who touched this CPF" is exactly the
  // sentence that must not end up in any of them.
  staffAudit: '/equipe/auditoria',
  customer: '/empresa',
  customerBeneficiaries: '/empresa/beneficiarios',
  // The only address in the product that names a record. It carries the opaque
  // `insured_person_id` and nothing else -- never a CPF, never a name -- and the
  // company it belongs to is not in the URL at all: it comes from the signed-in
  // principal's scope, so there is no address that switches tenants.
  customerPerson: '/empresa/beneficiarios/:pessoaId',
  customerMovements: '/empresa/movimentacoes',
  customerStatus: '/empresa/acompanhamento',
  customerSpreadsheetUpload: '/empresa/envio-de-planilha',
} as const;

/**
 * The address of one company, and of the form that adds a contract to it.
 *
 * Built here rather than by interpolating a string in a component, so the
 * pattern above and the link that satisfies it cannot drift apart. The argument
 * is the opaque uuid and the functions take nothing else -- there is no
 * overload that could accidentally be handed a slug or a CNPJ.
 *
 * `tab` opens the company on one of its sections, as `?aba=`; without it the
 * page opens on the first. A section is not a person, so it may sit in the
 * address -- and it is typed, so a link cannot name a tab the page lacks.
 */
export function staffCompanyPath(companyId: string, tab?: CompanyTab): string {
  const base = PATHS.staffCompany.replace(':empresaId', companyId);
  return tab === undefined ? base : `${base}?aba=${tab}`;
}

export function staffCompanyContractNewPath(companyId: string): string {
  return PATHS.staffCompanyContractNew.replace(':empresaId', companyId);
}

/**
 * The address of one segurado of one company: two opaque uuids. `personId` is
 * an `insured_person_id`, any one of the person's rows -- the page finds the
 * others.
 */
export function staffCompanyPersonPath(companyId: string, personId: string): string {
  return PATHS.staffCompanyPerson.replace(':empresaId', companyId).replace(':pessoaId', personId);
}

/**
 * The eSocial screen on its portal-walk tab, optionally narrowed to one company
 * and opened on one run. Both are opaque uuids: what a walk was asked about --
 * a CPF filter above all -- never reaches the address, so a link pasted into a
 * conversation carries nothing but which run to look at.
 */
export function staffEsocialPortalWalkPath(
  companyId?: string | null,
  runId?: string | null,
): string {
  const query = new URLSearchParams({ aba: 'portal' satisfies EsocialTab });
  if (companyId) {
    query.set('empresa', companyId);
  }
  if (runId) {
    query.set('percurso', runId);
  }
  return `${PATHS.staffEsocial}?${query.toString()}`;
}

/** The address of one economic group. The opaque uuid and nothing else. */
export function staffGroupPath(groupId: string): string {
  return PATHS.staffGroup.replace(':grupoId', groupId);
}

/**
 * The address of one contract's month in the submissions rhythm.
 *
 * `cycleMonth` rides along as a query parameter so an analyst working a month
 * other than the current one does not land on the wrong cycle's fechamento.
 * It is a month and not a person, so it may sit in the address; the contract
 * itself is the opaque uuid and nothing else, per the rule at the top of this
 * file.
 */
export function staffSubmissionContractPath(contractId: string, cycleMonth: string): string {
  const base = PATHS.staffSubmissionContract.replace(':contratoId', contractId);
  return `${base}?ciclo=${encodeURIComponent(cycleMonth)}`;
}

/**
 * The address of one company's month with one insurer, when that insurer takes
 * a single file for all of the company's contracts. Two opaque uuids and the
 * month, for the same reasons as a contract's.
 */
export function staffSubmissionConsolidatedPath(
  companyId: string,
  insurerId: string,
  cycleMonth: string,
): string {
  const base = PATHS.staffSubmissionConsolidated
    .replace(':empresaId', companyId)
    .replace(':operadoraId', insurerId);
  return `${base}?ciclo=${encodeURIComponent(cycleMonth)}`;
}

/**
 * The same one level up: an economic group's month with an insurer that takes
 * a single file for every contract of the group's companies.
 */
export function staffSubmissionGroupPath(
  groupId: string,
  insurerId: string,
  cycleMonth: string,
): string {
  const base = PATHS.staffSubmissionGroup
    .replace(':grupoId', groupId)
    .replace(':operadoraId', insurerId);
  return `${base}?ciclo=${encodeURIComponent(cycleMonth)}`;
}

export const AREA_HOME: Record<Area, string> = {
  staff: PATHS.staff,
  customer: PATHS.customer,
};

/**
 * Where to send someone after they sign in.
 *
 * `intended` is where they were trying to go before being asked to sign in, and
 * it is honoured even when it belongs to the other area. Sending them home
 * instead would be quieter and worse: someone who followed a link to the
 * back-office and ended up on their own dashboard concludes the link was
 * broken. Taking them to the address they asked for means
 * [`RequireArea`](./RequireArea.tsx) answers the actual question -- this area
 * is not yours, here is why, here is yours -- which is a thing a person can act
 * on.
 *
 * That matters more than it looks because the session lives only in memory. A
 * pasted link or a bookmark is a fresh page load with no session, so *every*
 * cross-area deep link arrives through here rather than through the in-app
 * guard.
 */
export function destinationAfterSignIn(area: Area, intended: string | null): string {
  // Bouncing back to the login page would be a loop.
  return intended === null || intended === PATHS.login ? AREA_HOME[area] : intended;
}
